// API client for communicating with the Worker API

import { getAuthToken, clearAuth } from './auth'
import router from '@/router'

const API_BASE = '/api'

// ==================== CACHING LAYER ====================
// localStorage-based cache for API responses with TTL support
const CACHE_PREFIX = 'tt_tasks_cache_'
const CACHE_INDEX_KEY = `${CACHE_PREFIX}index`

const CACHE_CONFIG = {
  // List endpoints - cache for 5 minutes
  'projects': { ttl: 5 * 60 * 1000 },
  'tasks': { ttl: 3 * 60 * 1000 },
  'milestones': { ttl: 5 * 60 * 1000 },
  'deadlines': { ttl: 5 * 60 * 1000 },
  'tags': { ttl: 10 * 60 * 1000 }, // Tags change less frequently
  'holidays': { ttl: 10 * 60 * 1000 },
  'time-off': { ttl: 5 * 60 * 1000 },
  'attachments': { ttl: 2 * 60 * 1000 },
  // Bulk endpoints - cache for 3 minutes
  'bulk': { ttl: 3 * 60 * 1000 }
}

// Manage cache index (list of all cache keys)
function getCacheIndex() {
  try {
    const index = localStorage.getItem(CACHE_INDEX_KEY)
    return index ? JSON.parse(index) : []
  } catch (e) {
    return []
  }
}

function addToCacheIndex(key) {
  const index = getCacheIndex()
  if (!index.includes(key)) {
    index.push(key)
    localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(index))
  }
}

function removeFromCacheIndex(key) {
  const index = getCacheIndex()
  const filtered = index.filter(k => k !== key)
  localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(filtered))
}

function getCacheKey(url) {
  return `${CACHE_PREFIX}${url}`
}

function getCacheConfig(url) {
  if (url.includes('/all')) return CACHE_CONFIG.bulk
  
  const resource = url.split('/')[2]?.split('?')[0] // Extract resource name
  return CACHE_CONFIG[resource] || null
}

function getFromCache(key) {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) return null
    
    const parsed = JSON.parse(cached)
    const now = Date.now()
    
    if (now > parsed.expiresAt) {
      localStorage.removeItem(key)
      removeFromCacheIndex(key)
      return null
    }
    
    return parsed.data
  } catch (e) {
    // If there's an error parsing, remove the corrupted cache entry
    localStorage.removeItem(key)
    removeFromCacheIndex(key)
    return null
  }
}

function setCache(key, data, ttl) {
  try {
    const cacheEntry = {
      data,
      expiresAt: Date.now() + ttl
    }
    localStorage.setItem(key, JSON.stringify(cacheEntry))
    addToCacheIndex(key)
  } catch (e) {
    // If localStorage is full, clear expired entries and try again
    cleanExpiredCache()
    try {
      const cacheEntry = {
        data,
        expiresAt: Date.now() + ttl
      }
      localStorage.setItem(key, JSON.stringify(cacheEntry))
      addToCacheIndex(key)
    } catch (e2) {
      // If still failing, do nothing (gracefully degrade to no caching)
      console.warn('Failed to cache data in localStorage:', e2)
    }
  }
}

function cleanExpiredCache() {
  const index = getCacheIndex()
  const now = Date.now()
  
  index.forEach(key => {
    try {
      const cached = localStorage.getItem(key)
      if (cached) {
        const parsed = JSON.parse(cached)
        if (now > parsed.expiresAt) {
          localStorage.removeItem(key)
        }
      } else {
        // Key is in index but not in storage, clean it up
        localStorage.removeItem(key)
      }
    } catch (e) {
      // Remove corrupted entries
      localStorage.removeItem(key)
    }
  })
  
  // Rebuild index with only valid keys
  const validKeys = index.filter(key => {
    try {
      return localStorage.getItem(key) !== null
    } catch {
      return false
    }
  })
  localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(validKeys))
}

function invalidateCache(pattern) {
  // Invalidate all cache entries matching the pattern
  const index = getCacheIndex()
  const keysToDelete = []
  
  index.forEach(key => {
    if (key.includes(pattern)) {
      keysToDelete.push(key)
    }
  })
  
  keysToDelete.forEach(key => {
    localStorage.removeItem(key)
    removeFromCacheIndex(key)
  })
}

// More explicit cache invalidation for task updates
function invalidateTaskCaches() {
  // Delete all keys that might contain task data
  const index = getCacheIndex()
  const keysToDelete = []
  
  index.forEach(key => {
    if (key.includes('/api/tasks') || key.includes('/milestones') || 
        key.includes('/deadlines') || key.includes('/api/projects')) {
      keysToDelete.push(key)
    }
  })
  
  keysToDelete.forEach(key => {
    localStorage.removeItem(key)
    removeFromCacheIndex(key)
  })
}

// Export cache utilities for manual invalidation if needed
export function clearCache() {
  const index = getCacheIndex()
  index.forEach(key => {
    localStorage.removeItem(key)
  })
  localStorage.removeItem(CACHE_INDEX_KEY)
}

export function invalidateCacheByResource(resource) {
  invalidateCache(`/api/${resource}`)
}

// ==================== HTTP HELPERS ====================

function getHeaders() {
  const headers = { 'Content-Type': 'application/json' }
  const token = getAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function handleResponse(response) {
  if (response.status === 401) {
    // Token is invalid or expired
    clearAuth()
    // Redirect to login page
    await router.push('/login')
    throw new Error('Session expired - please log in again')
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }
  return response.json()
}

async function fetchWithCache(url, options = {}) {
  const cacheKey = getCacheKey(url)
  const config = getCacheConfig(url)
  
  // Only cache GET requests
  if (!options.method || options.method === 'GET') {
    if (config) {
      const cached = getFromCache(cacheKey)
      if (cached) {
        return cached
      }
    }
  }
  
  const response = await fetch(url, options)
  const data = await handleResponse(response)
  
  // Cache successful GET responses
  if ((!options.method || options.method === 'GET') && config) {
    setCache(cacheKey, data, config.ttl)
  }
  
  return data
}

// ==================== API ENDPOINTS ====================

// Projects
export async function getProjects() {
  return fetchWithCache(`${API_BASE}/projects`, {
    headers: getHeaders()
  })
}

export async function getProject(id) {
  return fetchWithCache(`${API_BASE}/projects/${id}`, {
    headers: getHeaders()
  })
}

export async function createProject(data) {
  const response = await fetch(`${API_BASE}/projects`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/projects')
  return result
}

export async function updateProject(id, data) {
  const response = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/projects')
  return result
}

export async function deleteProject(id) {
  const response = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/projects')
  invalidateCache('/api/tasks') // Tasks may be affected
  invalidateCache('/api/milestones') // Milestones may be affected
  return result
}

export async function deleteProjectPermanently(id) {
  const response = await fetch(`${API_BASE}/projects/${id}/permanent`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/projects')
  invalidateCache('/api/tasks')
  invalidateCache('/api/milestones')
  return result
}

// Milestones
export async function getMilestones(projectId = null) {
  const url = projectId 
    ? `${API_BASE}/milestones?project_id=${projectId}`
    : `${API_BASE}/milestones`
  return fetchWithCache(url, {
    headers: getHeaders()
  })
}

export async function createMilestone(data) {
  const response = await fetch(`${API_BASE}/milestones`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/milestones')
  return result
}

export async function updateMilestone(id, data) {
  const response = await fetch(`${API_BASE}/milestones/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/milestones')
  return result
}

export async function deleteMilestone(id) {
  const response = await fetch(`${API_BASE}/milestones/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/milestones')
  invalidateCache('/api/tasks') // Tasks may reference this milestone
  return result
}

// Deadlines
export async function getDeadlines(projectId = null) {
  const url = projectId 
    ? `${API_BASE}/deadlines?project_id=${projectId}`
    : `${API_BASE}/deadlines`
  return fetchWithCache(url, {
    headers: getHeaders()
  })
}

export async function createDeadline(data) {
  const response = await fetch(`${API_BASE}/deadlines`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/deadlines')
  return result
}

export async function updateDeadline(id, data) {
  const response = await fetch(`${API_BASE}/deadlines/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/deadlines')
  return result
}

export async function deleteDeadline(id) {
  const response = await fetch(`${API_BASE}/deadlines/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/deadlines')
  return result
}

// Tasks
export async function getTasks(filters = {}) {
  const params = new URLSearchParams()
  if (filters.project_id) params.append('project_id', filters.project_id)
  if (filters.milestone_id) params.append('milestone_id', filters.milestone_id)
  if (filters.completed !== undefined) params.append('completed', filters.completed)
  
  const url = `${API_BASE}/tasks${params.toString() ? '?' + params.toString() : ''}`
  return fetchWithCache(url, {
    headers: getHeaders()
  })
}

export async function createTask(data) {
  const response = await fetch(`${API_BASE}/tasks`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  // Invalidate all task-related caches
  invalidateTaskCaches()
  return result
}

export async function updateTask(id, data) {
  const response = await fetch(`${API_BASE}/tasks/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  
  // Use the more thorough cache invalidation for task updates
  invalidateTaskCaches()
  
  return result
}

export async function deleteTask(id) {
  const response = await fetch(`${API_BASE}/tasks/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  // Invalidate all task-related caches
  invalidateTaskCaches()
  return result
}

// Time Off
export async function getTimeOff(upcoming = false) {
  const url = upcoming 
    ? `${API_BASE}/time-off?upcoming=true`
    : `${API_BASE}/time-off`
  return fetchWithCache(url, {
    headers: getHeaders()
  })
}

export async function createTimeOff(data) {
  const response = await fetch(`${API_BASE}/time-off`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/time-off')
  return result
}

export async function updateTimeOff(id, data) {
  const response = await fetch(`${API_BASE}/time-off/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/time-off')
  return result
}

export async function deleteTimeOff(id) {
  const response = await fetch(`${API_BASE}/time-off/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/time-off')
  return result
}

// Holidays
export async function getHolidays() {
  return fetchWithCache(`${API_BASE}/holidays`, {
    headers: getHeaders()
  })
}

export async function createHoliday(data) {
  const response = await fetch(`${API_BASE}/holidays`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/holidays')
  return result
}

export async function updateHoliday(id, data) {
  const response = await fetch(`${API_BASE}/holidays/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/holidays')
  return result
}

export async function deleteHoliday(id) {
  const response = await fetch(`${API_BASE}/holidays/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/holidays')
  return result
}

// Tags
export async function getTags() {
  return fetchWithCache(`${API_BASE}/tags`, {
    headers: getHeaders()
  })
}

export async function createTag(data) {
  const response = await fetch(`${API_BASE}/tags`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/tags')
  return result
}

export async function updateTag(id, data) {
  const response = await fetch(`${API_BASE}/tags/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  })
  const result = await handleResponse(response)
  invalidateCache('/api/tags')
  return result
}

export async function deleteTag(id) {
  const response = await fetch(`${API_BASE}/tags/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/tags')
  return result
}

export async function getProjectTags(projectId) {
  return fetchWithCache(`${API_BASE}/projects/${projectId}/tags`, {
    headers: getHeaders()
  })
}

// Fetch all project tags in bulk (performance optimization for avoiding N+1 queries)
export async function getAllProjectTags() {
  return fetchWithCache(`${API_BASE}/projects/tags/all`, {
    headers: getHeaders()
  })
}

export async function updateProjectTags(projectId, tagIds) {
  const response = await fetch(`${API_BASE}/projects/${projectId}/tags`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ tag_ids: tagIds })
  })
  const result = await handleResponse(response)
  invalidateCache('/api/projects')
  invalidateCache('/api/tags')
  return result
}
// Attachments
export async function getTaskAttachments(taskId) {
  return fetchWithCache(`${API_BASE}/tasks/${taskId}/attachments`, {
    headers: getHeaders()
  })
}

export async function getAttachmentCounts() {
  return fetchWithCache(`${API_BASE}/attachments/counts`, {
    headers: getHeaders()
  })
}

export async function uploadTaskAttachment(taskId, file, onProgress) {
  const formData = new FormData()
  formData.append('file', file)
  
  const token = getAuthToken()
  const headers = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    
    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          onProgress(percentComplete)
        }
      })
    }
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          invalidateCache('/api/attachments') // Invalidate attachment cache
          invalidateCache('/api/attachments/counts') // Invalidate attachment counts
          resolve(response)
        } catch (e) {
          reject(new Error('Invalid response from server'))
        }
      } else if (xhr.status === 401) {
        clearAuth()
        router.push('/login')
        reject(new Error('Session expired - please log in again'))
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.error || `HTTP ${xhr.status}`))
        } catch (e) {
          reject(new Error(`HTTP ${xhr.status}`))
        }
      }
    })
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error'))
    })
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'))
    })
    
    xhr.open('POST', `${API_BASE}/tasks/${taskId}/attachments`)
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
    xhr.send(formData)
  })
}

export async function deleteTaskAttachment(taskId, attachmentId) {
  const response = await fetch(`${API_BASE}/tasks/${taskId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: getHeaders()
  })
  const result = await handleResponse(response)
  invalidateCache('/api/attachments')
  invalidateCache('/api/attachments/counts')
  return result
}